/* rehypeWordFade (Wave 3.6) — a standard rehype plugin that wraps every
   word of a hast text node in <span class="facility-ai-word"> so styles.css
   can fade each word in as streaming appends it.

   Why a hand-rolled walker instead of unist-util-visit: the package is only
   a transitive dependency here (its installed major is also ESM-only),
   so importing it directly would be fragile under different package
   managers/hoops. The tree shape we need is trivial (element.children /
   text.value), so a ~30-line recursive walk avoids a new dependency
   entirely — explicitly allowed by the task.

   Keep-alive golden rule: the spans exist ONLY while a message is
   streaming (MemoizedMarkdown drops this plugin the moment isStreaming
   flips false), so the animation can never replay inside a keep-alive
   (display:none) tab — the elements unmount with the stream.

   Reconciliation note: while streaming, the tail block re-parses per frame
   and its spans are re-created positionally — React reuses the existing
   DOM nodes for the unchanged prefix (CSS animations only run on newly
   inserted elements), so only the appended words actually fade in. */

const SKIP_TAGS = new Set(["code", "pre", "svg", "kbd"]);

/* "Hello  world" → [span("Hello"), text("  "), span("world")] — whitespace
   runs stay plain text nodes so wrapping/breaking is byte-identical to the
   untransformed render. */
const splitWords = (value) => {
  const parts = value.split(/(\s+)/);
  const result = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (!part) continue;
    if (/^\s+$/.test(part)) {
      result.push({ type: "text", value: part });
    } else {
      result.push({
        type: "element",
        tagName: "span",
        properties: { className: ["facility-ai-word"] },
        children: [{ type: "text", value: part }],
      });
    }
  }
  return result;
};

const transformNode = (node) => {
  if (!node || !Array.isArray(node.children)) return;
  let changed = false;
  const nextChildren = [];
  for (let index = 0; index < node.children.length; index += 1) {
    const child = node.children[index];
    if (
      child &&
      child.type === "text" &&
      typeof child.value === "string" &&
      child.value.trim()
    ) {
      changed = true;
      const words = splitWords(child.value);
      for (let wordIndex = 0; wordIndex < words.length; wordIndex += 1) {
        nextChildren.push(words[wordIndex]);
      }
    } else {
      if (child && child.type === "element" && !SKIP_TAGS.has(child.tagName)) {
        transformNode(child);
      }
      nextChildren.push(child);
    }
  }
  if (changed) node.children = nextChildren;
};

function rehypeWordFade() {
  return (tree) => {
    transformNode(tree);
  };
}

export default rehypeWordFade;
